import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Download, Trash2, Users, Bell, Search, Mail } from 'lucide-react';

export default function WaitlistViewer() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ community: 0, courses: {} });
  const [filter, setFilter] = useState('all'); // 'all' | 'community' | course slug
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get('/admin/waitlist').then(r => r.data),
        api.get('/waitlist/counts').then(r => r.data),
      ]);
      setRows(a || []);
      setCounts(b || { community: 0, courses: {} });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return (rows || []).filter(r => {
      if (filter === 'all') {} else if (filter === 'community') {
        if (r.type !== 'community') return false;
      } else {
        if (r.type !== 'course' || r.course_slug !== filter) return false;
      }
      if (q && !(r.email || '').toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [rows, filter, q]);

  const courseSlugs = useMemo(() => {
    const s = new Set();
    rows.forEach(r => { if (r.type === 'course' && r.course_slug) s.add(r.course_slug); });
    return Array.from(s).sort();
  }, [rows]);

  const exportCsv = async () => {
    const params = new URLSearchParams();
    if (filter === 'community') params.set('type', 'community');
    else if (filter !== 'all') { params.set('type', 'course'); params.set('course_slug', filter); }
    const url = `/admin/waitlist/export${params.toString() ? '?' + params.toString() : ''}`;
    const resp = await api.get(url, { responseType: 'blob' });
    const blob = new Blob([resp.data], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `waitlist_${filter}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this waitlist entry?')) return;
    await api.delete(`/admin/waitlist/${id}`);
    load();
  };

  const totalCourses = Object.values(counts.courses || {}).reduce((a, b) => a + b, 0);

  return (
    <div data-testid="waitlist-viewer">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.18em] uppercase text-muted-kc">Waitlist</div>
          <div className="font-display uppercase text-2xl mt-1">Interest Tracking</div>
          <div className="text-sm text-muted-kc mt-1">Emails captured from Coming Soon bells and the Community waitlist.</div>
        </div>
        <button onClick={exportCsv} className="h-10 px-4 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-sm font-semibold bg-ink text-white hover:bg-black/90 transition-colors" data-testid="waitlist-export"><Download size={14}/> Export CSV</button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="waitlist-summary">
        <Tile icon={Users} label="Total signups" value={rows.length} />
        <Tile icon={Mail} label="Community" value={counts.community || 0} />
        <Tile icon={Bell} label="Course waitlists" value={totalCourses} />
        <Tile icon={Bell} label="Hottest course" value={Object.entries(counts.courses || {}).sort((a, b) => b[1] - a[1])[0]?.[0] || '—'} />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="waitlist-filters">
        <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>All ({rows.length})</FilterChip>
        <FilterChip active={filter === 'community'} onClick={() => setFilter('community')}>Community ({counts.community || 0})</FilterChip>
        {courseSlugs.map((slug) => (
          <FilterChip key={slug} active={filter === slug} onClick={() => setFilter(slug)}>{slug} ({counts.courses?.[slug] || 0})</FilterChip>
        ))}
        <div className="relative ml-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-kc"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email…" className="h-9 pl-9 pr-3 rounded-[var(--kc-radius-sm)] border border-kc-strong text-sm w-56" data-testid="waitlist-search"/>
        </div>
      </div>

      <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-2 text-left text-xs uppercase tracking-wider">
            <tr><th className="p-3">Email</th><th className="p-3">Type</th><th className="p-3">Course</th><th className="p-3">Signed up</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-6 text-center text-muted-kc">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-kc">No entries yet. Once people hit the bell on a coming-soon course, they’ll appear here.</td></tr>}
            {filtered.map((r) => (
              <tr key={r.id} className="border-t border-kc">
                <td className="p-3 font-semibold">{r.email}</td>
                <td className="p-3 capitalize"><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${r.type === 'community' ? 'bg-gold-soft text-ink' : 'bg-ink text-white'}`}>{r.type}</span></td>
                <td className="p-3 text-muted-kc">{r.course_title || r.course_slug || '—'}</td>
                <td className="p-3 text-muted-kc text-xs">{r.created_at ? new Date(r.created_at).toLocaleString() : ''}</td>
                <td className="p-3 text-right"><button onClick={() => remove(r.id)} className="text-[color:var(--kc-danger)] hover:underline text-xs font-semibold inline-flex items-center gap-1" data-testid={`waitlist-del-${r.id}`}><Trash2 size={12}/> Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Tile = ({ icon: Icon, label, value }) => (
  <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-4">
    <div className="flex items-center justify-between">
      <div className="text-[10px] tracking-[0.18em] uppercase text-muted-kc">{label}</div>
      <Icon size={14} className="text-gold"/>
    </div>
    <div className="font-display text-3xl mt-1.5 truncate">{value}</div>
  </div>
);

const FilterChip = ({ active, onClick, children }) => (
  <button onClick={onClick} className={`px-3 h-8 text-xs font-semibold uppercase tracking-wider rounded-full transition-colors ${active ? 'bg-ink text-white' : 'bg-surface-2 text-ink-2 hover:bg-kc'}`}>
    {children}
  </button>
);
